import { z } from 'zod';
import { uuidSchema } from './common.js';
import { MAX_AMOUNT_CENTS, PROGRAM_TYPES, expirySchema, programRulesSchema } from './program-rules.js';
/**
 * Program and reward endpoints — API-CONTRACT.md §3 (v1).
 *
 * `rulesJson` is validated by the discriminated union in program-rules.ts, and
 * the union is `strictObject`, so a rule key that does not exist is a write-time
 * failure rather than a silently ignored setting.
 */
/**
 * A policy other than NONE needs a day count — the same rule as `expirySchema`.
 *
 * Without it, `FIXED_DAYS` with `expiryDays: null` was accepted and stored, and
 * the expiry job (which selects `expiryDays: { not: null }`) skipped it: a
 * merchant saw an expiry policy that nothing enforced.
 */
const hasExpiryDays = (v) => v.expiryPolicy === 'NONE' || v.expiryDays !== null;
const expiryDaysMessage = {
    message: 'expiryDays is required unless the expiry policy is NONE',
    path: ['expiryDays'],
};
export const createProgramSchema = z
    .strictObject({
    name: z.string().trim().min(1).max(120),
    type: z.enum(PROGRAM_TYPES),
    rules: programRulesSchema,
    expiryPolicy: z.enum(['NONE', 'FIXED_DAYS', 'ROLLING_INACTIVITY']).default('NONE'),
    expiryDays: z.number().int().min(1).max(3650).nullable().default(null),
})
    .refine(hasExpiryDays, expiryDaysMessage);
/**
 * A new version, not an edit. ProgramVersion is immutable and enrollments pin
 * to one, so changing the rules means creating the next version — AGENTS.md §4.3.
 */
export const createProgramVersionSchema = z
    .strictObject({
    rules: programRulesSchema,
    expiryPolicy: z.enum(['NONE', 'FIXED_DAYS', 'ROLLING_INACTIVITY']).default('NONE'),
    expiryDays: z.number().int().min(1).max(3650).nullable().default(null),
})
    .refine(hasExpiryDays, expiryDaysMessage);
export const activateProgramVersionSchema = z.strictObject({
    versionId: uuidSchema,
});
export const createRewardSchema = z.strictObject({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    imageUrl: z.url().max(500).nullable().optional(),
    costUnits: z.number().int().min(1).max(100_000),
    /**
     * Makes the reward a gift card of this value, in minor units — features/GIFT-CARDS.md
     * (`GIFT-6`). Redeeming it issues a card to the customer's own phone in the
     * same transaction as the points leave. The merchant sets the rate by setting
     * this and `costUnits`. Null for an ordinary reward.
     */
    giftCardAmount: z.number().int().min(1).max(MAX_AMOUNT_CENTS).nullable().optional(),
});
export const updateRewardSchema = z.strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    imageUrl: z.url().max(500).nullable().optional(),
    costUnits: z.number().int().min(1).max(100_000).optional(),
    giftCardAmount: z.number().int().min(1).max(MAX_AMOUNT_CENTS).nullable().optional(),
    isActive: z.boolean().optional(),
});
/**
 * "Apply this program to all brands" — features/TENANCY.md.
 *
 * features/TENANCY.md is explicit about the shape: "creates independent copies, not a shared
 * object, so brands can diverge later". So this is a *copy* operation with no
 * link back to the source. A brand that later raises its threshold must not move
 * anyone else's.
 *
 * `brandIds` is the explicit target list rather than an implied "all others".
 * "All brands" is what the button says; a request that names its targets is one
 * a merchant can be shown before it runs, and it keeps a brand from acquiring a
 * programme because it happened to exist when somebody clicked.
 */
export const copyProgramSchema = z.strictObject({
    brandIds: z.array(z.uuid()).min(1).max(50),
});
export { expirySchema };
//# sourceMappingURL=programs.js.map